import { Injectable } from '@angular/core';
import { RecaptchaComponent } from 'ng-recaptcha';
import { MainService } from './main-service.service';

declare const grecaptcha: any;

export const CAPTCHA_ENDPOINTS: string[] = ['update_proposal_link_status','verify_alternate_email','verify_alternate_mobile',
  'resend_alternate_otp','choose_plan_v2','send_split_plans_v2','send_contract_pdf','add_patient_address','add_patient',
  'add_pf_patient_info','new_add_patient_address','upload_cus_dl',
  'delete_cus_dl','create_payment_intent','create_payment_intent_customer','create_payment_intent_add_card',
  'create_payment_intent_finance','create_setup_intent_doctor','create_setup_intent_request','add_recurring_card',
  'add_payment_source','pay_split_pf','3d_secure_payment_through_request','open_pay_recurring','pay_overdue_amount','pay_due_amount',
  'open_payoff','check_date_of_birth','open_recurring_date_change','settle_now_and_save','upload_signature',
  'update_patient_email','update_down_payment_in_spr','save_pp_single_payment','customer_feedback','save_referral_data',
  'make_social_healthcare_payment','open_partial_payment','v1/provider/create-contract','v1/upload_signature',
  'v1/is_valid_mobile_number','is_valid_mobile_number','save_video_review','download_leads_reciept','add_pre_approval_leads',
  'charge_subscription_pp_request','upload_pp_docs','pp_verify_customer',
  'provider/contract/check_date_change_amount','provider/contract/change_recurring_date',
  'provider/payment/pay','provider/payment/pay_3d','staraligners/customer/verify_bank',
  'provider/create-contract','provider/product/add','product/add',
  'provider/product/add_dynamic_product'];

export type CaptchaCallback = (token: string) => Promise<any> | any;

@Injectable({
  providedIn: 'root',
})
export class CaptchaService {
  private widget: RecaptchaComponent | null = null;
  private widgetReadyResolvers: Array<(widget: RecaptchaComponent) => void> = [];
  private pendingResolve: ((token: string) => void) | null = null;
  private pendingReject: ((error: Error) => void) | null = null;
  private pendingExecution: Promise<string> | null = null;
  private tempDataCaptchaValidation: { funcToCall?: CaptchaCallback } | null = null;

  constructor(private mainService: MainService) {}

  requiresCaptcha(url: string): boolean {
    return CAPTCHA_ENDPOINTS.some((endpoint) => url.includes(endpoint));
  }

  attachCaptchaToBody(body: any, token: string, field = 'captcha'): any {
    if (body instanceof FormData) {
      const formData = new FormData();
      body.forEach((value, key) => formData.append(key, value));
      formData.append(field, token);
      return formData;
    }

    if (typeof body === 'string') {
      const encoded = encodeURIComponent(token);
      return body ? `${body}&${field}=${encoded}` : `${field}=${encoded}`;
    }

    if (body && typeof body === 'object') {
      return { ...body, [field]: token };
    }

    return { [field]: token };
  }

  registerWidget(widget: RecaptchaComponent) {
    this.widget = widget;
    this.widgetReadyResolvers.forEach((resolve) => resolve(widget));
    this.widgetReadyResolvers = [];
  }

  unregisterWidget(widget?: RecaptchaComponent) {
    if (!widget || this.widget === widget) {
      this.widget = null;
    }
  }

  handleCaptchaExpired() {
    console.log('Captcha expired! Resetting...');
    this.rejectPending(new Error('Captcha expired'));
    this.resetCaptcha();
  }

  handleCaptchaError() {
    this.mainService.customPopups('Captcha authentication failed', 1);
    this.rejectPending(new Error('Captcha authentication failed'));
    this.resetCaptcha();
  }

  resetCaptcha() {
    try {
      if (this.widget) {
        this.widget.reset();
        return;
      }
      if (typeof grecaptcha !== 'undefined') {
        grecaptcha.reset();
      }
    } catch (error) {
      console.error('Error resetting reCAPTCHA widget:', error);
    }
  }

  async executeCaptcha(): Promise<string> {
    if (this.pendingExecution) {
      return this.pendingExecution;
    }

    this.pendingExecution = this.runExecute().finally(() => {
      this.pendingExecution = null;
    });

    return this.pendingExecution;
  }

  async handleCaptchaResolved(captchaResponse: string | null) {
    if (!captchaResponse) {
      if (this.pendingResolve) {
        this.handleCaptchaExpired();
      }
      return;
    }

    if (this.pendingResolve) {
      this.pendingResolve(captchaResponse);
      this.clearPending();
      this.resetCaptcha();
    }

    if (this.tempDataCaptchaValidation?.funcToCall) {
      try {
        await this.tempDataCaptchaValidation.funcToCall(captchaResponse);
        this.tempDataCaptchaValidation = null;
      } catch (error) {
        console.error('Error executing funcToCall:', error);
      }
    }
  }

  async withCaptcha(payload: any, field = 'captcha'): Promise<any> {
    const token = await this.executeCaptcha();
    payload[field] = token;
    return payload;
  }

  executeThen(funcToCall: CaptchaCallback): Promise<string> {
    this.tempDataCaptchaValidation = { funcToCall };
    return this.executeCaptcha();
  }

  private async runExecute(): Promise<string> {
    try {
      const widget = await this.waitForWidget();
      return await new Promise<string>((resolve, reject) => {
        this.pendingResolve = resolve;
        this.pendingReject = reject;
        widget.execute();
      });
    } catch (err) {
      console.log('ng-recaptcha execute error:', err);
      this.mainService.customPopups('Captcha authentication failed', 1);
      this.clearPending();
      throw err;
    }
  }

  private waitForWidget(timeoutMs = 10000): Promise<RecaptchaComponent> {
    if (this.widget) {
      return Promise.resolve(this.widget);
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('reCAPTCHA is not ready'));
      }, timeoutMs);

      this.widgetReadyResolvers.push((widget) => {
        clearTimeout(timer);
        resolve(widget);
      });
    });
  }

  private rejectPending(error: Error) {
    if (this.pendingReject) {
      this.pendingReject(error);
    }
    this.clearPending();
  }

  private clearPending() {
    this.pendingResolve = null;
    this.pendingReject = null;
  }
}
